/* eslint-disable no-console */
import _ from 'lodash';
import { batch } from 'react-redux';

import { handleErrors } from '../../actions/ErrorActions';
import { addSuccess } from '../../actions/SuccessActions';
import { clearWarnings, enableWarnings, updateAllWarnings } from '../../actions/WarningActions';
import {
  createVirtualEntity as createVirtualEntityApi,
  deleteReport as deleteReportApi,
  exportReport as exportReportApi,
  getNewReportTemplate,
  getReportModel,
  getReportModelList,
  importReportApi,
  saveReport as saveReportApi,
} from '../../api/report-designer';
import { setFetchErrorProp } from '../../form-designer/utils/actions/form-manager.actions.utils';
import { changeURLParam } from '../../utils/commonUtils';
import { downloadFile } from '../../utils/domUtils';
import { isNilOrEmpty } from '../../utils/ramdaUtils';
import {
  DEFAULT_CHILDREN_CONTROLS,
  REPORT_LAYOUT_ID,
} from '../constants/report-designer.constants';
import {
  getControlProperty,
  getOriginalControlProperty,
  getParentChildrenControlTypes,
} from '../selectors/report-designer.selectors';
import {
  convertEntityFieldsTranslation,
  createDefaultChildrenControls,
  createNewControl,
  getNewParentChildrenNodesForMovedControl,
  getPayloadForCreateVirtualEntity,
  getReportModelForSave,
  preprocessReportModel,
} from '../utils/report-designer.utils';
import { validateReportControls } from '../utils/validation.utils';

export const SET_REPORT_DATA = 'SET_REPORT_DATA';
export const SET_ORIGINAL_REPORT_MODEL = 'SET_ORIGINAL_REPORT_MODEL';
export const SET_SELECTED_CONTROL_OBJECT = 'SET_SELECTED_CONTROL_OBJECT';
export const SET_SELECTED_TAB_INDEX = 'SET_SELECTED_TAB_INDEX';
export const UPDATE_CONTROL_PROPERTY = 'UPDATE_CONTROL_PROPERTY';
export const UPDATE_CONTROL_PROPERTIES = 'UPDATE_CONTROL_PROPERTIES';
export const UPDATE_CONTROL = 'UPDATE_CONTROL';
export const DELETE_CONTROLS = 'DELETE_CONTROLS';
export const CLEAR_REPORT_DESIGNER_STORE = 'CLEAR_REPORT_DESIGNER_STORE';
export const ADD_NEW_DISTRIBUTION_LIST_ROW = 'ADD_NEW_DISTRIBUTION_LIST_ROW';
export const ADD_NEW_DISTRIBUTION_LIST_ROWS = 'ADD_NEW_DISTRIBUTION_LIST_ROWS';
export const DELETE_DISTRIBUTION_LIST_ROW = 'DELETE_DISTRIBUTION_LIST_ROW';
export const ADD_VALIDATION_ERRORS = 'ADD_VALIDATION_ERRORS';
export const DELETE_VALIDATION_ERRORS = 'DELETE_VALIDATION_ERRORS';
export const SET_IS_REPORT_SUBMITTED = 'SET_IS_REPORT_SUBMITTED';

export const setReportData = (reportData) => ({
  type: SET_REPORT_DATA,
  payload: { reportData },
});

export const setOriginalReportModel = (reportModel) => ({
  type: SET_ORIGINAL_REPORT_MODEL,
  payload: { reportModel },
});

export const setSelectedControlObject = (id, hasControlWrapper) => ({
  type: SET_SELECTED_CONTROL_OBJECT,
  payload: {
    id,
    hasControlWrapper,
  },
});

export const setSelectedTabIndex = (index) => ({
  type: SET_SELECTED_TAB_INDEX,
  payload: {
    index,
  },
});

export const clearReportDesignerStore = () => ({
  type: CLEAR_REPORT_DESIGNER_STORE,
});

export const addDistributionListRow = ({ newRow }) => ({
  type: ADD_NEW_DISTRIBUTION_LIST_ROW,
  payload: { newRow },
});

export const addDistributionListRows = ({ rows }) => ({
  type: ADD_NEW_DISTRIBUTION_LIST_ROWS,
  payload: { rows },
});

export const setIsReportSubmitted = (flag) => ({
  type: SET_IS_REPORT_SUBMITTED,
  payload: { flag },
});

export const deleteDistributionListRow = ({ rowId }) => ({
  type: DELETE_DISTRIBUTION_LIST_ROW,
  payload: { rowId },
});

export const addValidationErrors = (validationErrors) => ({
  type: ADD_VALIDATION_ERRORS,
  payload: { validationErrors },
});

export const deleteValidationErrors = () => ({
  type: DELETE_VALIDATION_ERRORS,
});

export const loadReport = (reportId) => async (dispatch) => {
  const response = await getReportModel(reportId);

  if (response.hasError) {
    const responseErrors = setFetchErrorProp(response.errors);

    dispatch(handleErrors(responseErrors));
    return;
  }

  const { reportDefinition, entities, reportModel: reportModelEntityStructure } = response;
  const reportModelEntityHierarchy = reportModelEntityStructure.ReportDesign.ReportModel.Views;
  const entityFieldsTranslation = convertEntityFieldsTranslation(entities);

  const { reportModel, reportInfo } = reportDefinition;
  const defaultDataMember = Object.keys(entities)[0];

  const preprocessedReportModel = preprocessReportModel(reportModel, defaultDataMember);

  batch(() => {
    dispatch(
      setReportData({
        reportModel: preprocessedReportModel,
        reportInfo,
        reportModelEntityHierarchy,
        reportModelEntityDefinition: entities,
        entityFieldsTranslation,
      })
    );
    dispatch(setOriginalReportModel(preprocessedReportModel));
  });
};

export const updateControlProperty = (id, propName, newPropValue) => (dispatch, getState) => {
  const currentStoreValue = getControlProperty(getState(), { id, prop: propName });
  const originalPropValue = getOriginalControlProperty(getState(), { id, prop: propName });

  const hasValueChanged =
    currentStoreValue !== newPropValue &&
    !(isNilOrEmpty(newPropValue) && isNilOrEmpty(currentStoreValue));

  if (!hasValueChanged) {
    return;
  }

  const valueToStore =
    _.isNil(originalPropValue) && newPropValue === '' ? originalPropValue : newPropValue;

  dispatch({
    type: UPDATE_CONTROL_PROPERTY,
    payload: {
      id,
      propName,
      newPropValue: valueToStore,
    },
  });
};

export const deleteControls = (ids) => ({
  type: DELETE_CONTROLS,
  payload: {
    ids,
  },
});

export const updateControlProperties = (id, newValues) => ({
  type: UPDATE_CONTROL_PROPERTIES,
  payload: {
    id,
    newValues,
  },
});

export const changeControlPositionByButton = (id, moveType) => (dispatch, getState) => {
  const state = getState();
  const { parentId, controlType } = state.reportDesigner.reportModel[id] || {};
  const parentChildrenControlTypes = getParentChildrenControlTypes(state, { parentId });

  const newParentChildrenNodes = getNewParentChildrenNodesForMovedControl(
    parentChildrenControlTypes,
    id,
    moveType,
    controlType
  );

  dispatch(updateControlProperty(parentId, 'childrenNodes', newParentChildrenNodes));
};

export const deleteControl = (id, shouldSelectReportLayout = true) => (dispatch, getState) => {
  const { reportModel } = getState().reportDesigner;
  const { parentId, childrenNodes = [] } = reportModel[id];
  const { childrenNodes: parentChildrenNodes } = reportModel[parentId];

  const newParentChildrenNodes = parentChildrenNodes.filter((childId) => childId !== id);
  const idsForDeletion = [id, ...childrenNodes];

  batch(() => {
    dispatch(updateControlProperty(parentId, 'childrenNodes', newParentChildrenNodes));
    dispatch(deleteControls(idsForDeletion));
    shouldSelectReportLayout && dispatch(setSelectedControlObject(REPORT_LAYOUT_ID, false));
  });
};

export const updateControl = (id, controlProps) => ({
  type: UPDATE_CONTROL,
  payload: { id, controlProps },
});

export const addControl = ({ prevControlId, parentId, controlType, mappingValues }) => (
  dispatch,
  getState
) => {
  const { reportModel, reportModelEntityDefinition } = getState().reportDesigner;
  const { childrenNodes: parentChildrenNodes } = reportModel[parentId];
  const defaultDataMember = Object.keys(reportModelEntityDefinition)[0];

  const newControl = createNewControl({ controlType, parentId, mappingValues, defaultDataMember });
  const newControlId = newControl.controlId;

  const previousControlIndex = parentChildrenNodes.indexOf(prevControlId);
  const newParentChildrenNodes = [...parentChildrenNodes];

  previousControlIndex === -1
    ? newParentChildrenNodes.push(newControlId)
    : newParentChildrenNodes.splice(previousControlIndex + 1, 0, newControlId);

  const defaultControls = DEFAULT_CHILDREN_CONTROLS[controlType] || [];

  if (defaultControls.length) {
    const newChildControls = createDefaultChildrenControls({
      parentControl: newControl,
      parentId: newControl.controlId,
      defaultControls,
      defaultDataMember,
    });

    batch(() => {
      newChildControls.forEach((control) => dispatch(updateControl(control.controlId, control)));
    });
  }

  batch(() => {
    dispatch(updateControlProperty(parentId, 'childrenNodes', newParentChildrenNodes));
    dispatch(updateControl(newControlId, newControl));
    dispatch(setSelectedControlObject(newControlId, true));
  });
};

export const validateReport = ({ validateOnSave } = {}) => async (dispatch, getState) => {
  const { translations, reportDesigner } = getState();
  const { reportModel, validationErrors, isReportSubmitted } = reportDesigner;

  if (validateOnSave || isReportSubmitted) {
    !isReportSubmitted && dispatch(setIsReportSubmitted(true));

    const newValidationErrors = await validateReportControls({ reportModel, translations });

    if (newValidationErrors.length) {
      dispatch(addValidationErrors(newValidationErrors));
      dispatch(updateAllWarnings(newValidationErrors));
      dispatch(enableWarnings());
    }

    if (validationErrors.length && !newValidationErrors.length) {
      dispatch(clearWarnings());
      dispatch(deleteValidationErrors());
    }

    return !newValidationErrors.length;
  }

  return [];
};

export const saveReport = ({ isSaveAs = false, reportName = '' }) => async (dispatch, getState) => {
  try {
    const isReportValid = await dispatch(validateReport({ validateOnSave: true }));

    if (!isReportValid) {
      return;
    }

    const { reportModel, reportInfo } = getState().reportDesigner;

    const payloadReportModel = getReportModelForSave(reportModel, reportInfo, isSaveAs, reportName);

    const response = await saveReportApi(payloadReportModel);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    const { reportInfo: newReportInfo, reportModel: newReportModel } = response.reportDefinition;

    const preprocessedReportModel = preprocessReportModel(newReportModel);

    batch(() => {
      dispatch(
        setReportData({
          reportModel: preprocessedReportModel,
          reportInfo: newReportInfo,
        })
      );
      dispatch(setOriginalReportModel(preprocessedReportModel));
      dispatch(addSuccess({ message: 'Report has been saved successfully' }));
      dispatch(setIsReportSubmitted(false));
      if (isSaveAs) {
        changeURLParam({ paramName: 'reportId', paramValue: newReportInfo.reportId });
      }
    });
  } catch (error) {
    console.log(`saveReport() failed: ${error}`);
  }
};

export const exportReport = (reportId, reportName) => async (dispatch) => {
  try {
    const response = await exportReportApi(reportId);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    downloadFile(`${reportName}.xml`, response, 3);
    dispatch(addSuccess({ message: 'Exported Successfully' }));
  } catch (error) {
    console.log(`exportReport() failed: ${error}`);
  }
};

export const importReport = ({ file, redirectToReportDesigner }) => async (dispatch) => {
  try {
    const response = await importReportApi(file);

    const {
      data: {
        hasError,
        errors,
        reportDefinition,
        entities,
        reportModel: reportModelEntityStructure,
      },
    } = response;

    if (hasError) {
      throw errors;
    }

    const reportModelEntityHierarchy = reportModelEntityStructure.ReportDesign.ReportModel.Views;
    const entityFieldsTranslation = convertEntityFieldsTranslation(entities);
    const { reportModel, reportInfo } = reportDefinition;
    const defaultDataMember = Object.keys(entities)[0];

    const preprocessedReportModel = preprocessReportModel(reportModel, defaultDataMember);

    batch(() => {
      dispatch(
        setReportData({
          reportModel: preprocessedReportModel,
          reportInfo,
          reportModelEntityHierarchy,
          reportModelEntityDefinition: entities,
          entityFieldsTranslation,
        })
      );
    });

    redirectToReportDesigner();

    dispatch(addSuccess({ message: 'Report Imported Successfully' }));
  } catch (errors) {
    console.log(`importReport() failed: ${errors}`);

    if (Array.isArray(errors)) {
      throw errors;
    }

    throw new Error(`importReport() failed: ${errors}`);
  }
};

export const deleteReport = ({ redirectToPrevPage, id }) => async (dispatch) => {
  try {
    const response = await deleteReportApi(id);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    dispatch(addSuccess({ message: 'Report has been deleted successfully' }));
    redirectToPrevPage();
  } catch (error) {
    console.log(`deleteReport() failed: ${error}`);
  }
};

export const createNewReport = (reportName, reportModelName) => async (dispatch) => {
  const response = await getNewReportTemplate(reportModelName, reportName);

  if (response.hasError) {
    dispatch(handleErrors(response.errors));
    return;
  }

  const { reportInfo, reportModel } = response.reportDefinition;

  const saveReportResponse = await saveReportApi({
    createEntityView: false,
    publish: false,
    overwrite: false,
    report: { reportInfo, reportModel },
  });

  if (saveReportResponse.hasError) {
    dispatch(handleErrors(saveReportResponse.errors));
    return;
  }

  const newReportId = saveReportResponse.reportDefinition.reportInfo.reportId;

  return newReportId;
};

export const fetchReportModelList = () => async (dispatch) => {
  try {
    const response = await getReportModelList();

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return [];
    }

    const reportModelList = response.reportModels?.V_OPS_OPTION_STORE;

    return reportModelList.map(({ OPS_KEY, ReportModelName }) => ({
      label: ReportModelName,
      value: OPS_KEY,
    }));
  } catch (error) {
    console.log(`fetchReportModelList() failed: ${error}`);
    return [];
  }
};

export const createVirtualEntity = ({ virtualEntityName = '', handleModalClose }) => async (
  dispatch,
  getState
) => {
  const { reportModel, reportInfo } = getState().reportDesigner;

  const payload = getPayloadForCreateVirtualEntity({ reportModel, reportInfo, virtualEntityName });

  try {
    const response = await createVirtualEntityApi(payload);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    dispatch(addSuccess({ message: 'Virtual Entity Created Successfully' }));
    handleModalClose();
  } catch (error) {
    console.log(`createVirtualEntity() failed: ${error}`);
  }
};
